import AutorenewRounded from '@mui/icons-material/AutorenewRounded';
import KeyRounded from '@mui/icons-material/KeyRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ErrorState, LoadingRows } from '../../components/states';
import { Panel } from '../../components/ui';
import { useNotify } from '../../lib/notify';
import type { TenantDetail } from './api';

type TenantUser = Schemas['TenantUsers']['users'][number];

const usersKey = (tenantId: string) => ['platform', 'tenants', 'users', tenantId] as const;

/** A readable 16-char password (no ambiguous characters). */
function generatePassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

/**
 * Tenant → Users: list the shop's users and (Super admin) reset any user's password. The user must
 * change the password at next sign-in and is signed out of every device.
 */
export function UsersTab({ tenant }: { tenant: Pick<TenantDetail, 'id' | 'name'> }) {
  const { can } = useAuth();
  const manage = can('tenants.users.manage');
  const notify = useNotify();
  const q = useQuery({
    queryKey: usersKey(tenant.id),
    queryFn: () =>
      unwrap(api.GET('/platform/tenants/{id}/users', { params: { path: { id: tenant.id } } })),
  });

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const reset = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/platform/tenants/{id}/users/{userId}/reset-password', {
          params: { path: { id: tenant.id, userId } },
          body: { newPassword: password },
        }),
      ),
    onSuccess: () => {
      const u = q.data?.users.find((x) => x.id === userId);
      notify.success(
        `Password reset for ${u?.name ?? 'the user'} — they must change it at sign-in`,
      );
      setPassword('');
      void q.refetch();
    },
    onError: (e) => notify.error(e),
  });

  if (q.isPending) return <LoadingRows rows={4} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  const users = q.data.users;
  const label = (u: TenantUser) =>
    `${u.name}${u.email ? ` · ${u.email}` : ''}${u.isOwner ? ' · Owner' : ''}`;
  const canSubmit = Boolean(userId) && password.length >= 8 && !reset.isPending;

  return (
    <Panel
      title="Users"
      subtitle={`${String(users.length)} user${users.length === 1 ? '' : 's'} in ${tenant.name}.`}
    >
      <Stack spacing={2.5}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {users.map((u) => (
            <Box
              key={u.id}
              sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, minWidth: 0 }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 600 }} noWrap>
                  {u.name}
                </Typography>
                {u.isOwner && <Chip size="small" color="primary" label="Owner" />}
                <Chip
                  size="small"
                  color={u.status === 'ACTIVE' ? 'success' : 'default'}
                  label={u.status === 'ACTIVE' ? 'Active' : 'Deactivated'}
                />
                {u.mustChangePassword && <Chip size="small" label="Temporary password" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {[u.email, u.phone, u.roles.join(', ')].filter(Boolean).join(' · ')}
              </Typography>
            </Box>
          ))}
        </Box>

        {manage ? (
          <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>
            <Typography sx={{ fontWeight: 650, mb: 0.5 }}>Reset a user's password</Typography>
            <Typography variant="caption" color="text.secondary">
              Sets a new password; the user must change it at next sign-in and is signed out
              everywhere.
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 2, alignItems: { sm: 'flex-start' } }}
            >
              <TextField
                select
                label="User"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                sx={{ minWidth: 240, flex: 1 }}
              >
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {label(u)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="New password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={password.length > 0 && password.length < 8}
                helperText={
                  password.length > 0 && password.length < 8 ? 'At least 8 characters' : ' '
                }
                sx={{ flex: 1 }}
                slotProps={{
                  input: {
                    endAdornment: (
                      <Tooltip title="Generate a password">
                        <IconButton
                          aria-label="Generate password"
                          onClick={() => setPassword(generatePassword())}
                          edge="end"
                        >
                          <AutorenewRounded fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ),
                  },
                }}
              />
              <Button
                variant="contained"
                color="warning"
                startIcon={<KeyRounded />}
                disabled={!canSubmit}
                onClick={() => reset.mutate()}
                sx={{ mt: { sm: 1 } }}
              >
                Reset password
              </Button>
            </Stack>
          </Box>
        ) : (
          <Alert severity="info">Only a Super admin can reset a user's password.</Alert>
        )}
      </Stack>
    </Panel>
  );
}
